// eslint-disable-next-line import/no-cycle
import { ILoggedUser } from '@shared/interfaces/IUser';
import { LoginRequestStatuses } from '@shared/interfaces/ILoginReducerState';
import { getIsEmailAddress } from '@shared/utils/email.utils';
import { resetPostHogUser } from '@shared/utils/posthog.utils';
import { RESET_ALL_REDUCERS_ACTION_TYPE } from '../store';
import { projectService, translationService, userService } from '@src/servicesInitializer';
import { setLoggedUser, setLoginStateData } from '../slices/login.slice';
import { updateUserProjects } from '../slices/project.slice';
import { fetchAndSetWorkingProject } from './project.thunks';
import { getUserPreferences } from './userPreferences.thunks';
// eslint-disable-next-line import/no-cycle
import { initServicesDependentByUserData } from '../../utils/userDataBootstrap.utils';
import * as serviceWorkerRegistration from '../../serviceWorkerRegistration';
import { AppRoutes } from '../../constants/appRoutes.enum';
import { BridgeResult, mintTokensFromEngineSession } from '../../apis/engineSession.api';
import type { TwoFactorMethod } from '../../services/betterAuthClient';

export const logout = () => (dispatch) => {
	serviceWorkerRegistration.unregister();
	userService.removeLoggedUser();
	projectService.removeChosenProject();
	resetPostHogUser();
	dispatch({ type: RESET_ALL_REDUCERS_ACTION_TYPE });
};

const TOO_MANY_REQUESTS_STATUS_CODE: number = 429;
const UNAUTHORIZED_STATUS_CODE: number = 401;

/**
 * Neutral login failure. The engine deliberately does not say whether the identifier or the
 * password was wrong, and neither does this — the only distinction worth surfacing is "you are
 * being rate limited", which reveals nothing and is otherwise indistinguishable from a wrong
 * password to the user.
 */
const dispatchNeutralLoginError = (dispatch: IAuthThunkDispatch, statusCode: number | undefined): void => {
	const isRateLimited: boolean = statusCode === TOO_MANY_REQUESTS_STATUS_CODE;
	dispatch(
		setLoginStateData({
			loginRequestStatus: {
				state: LoginRequestStatuses.ERROR,
				statusCode,
				responseMessage: translationService.get(
					isRateLimited ? 'login_error_tooManyAttempts' : 'login_error_invalidCredentials'
				),
			},
		})
	);
};

/**
 * What the auth screens carry between routes: where the user was headed before login intercepted
 * them, plus whichever hint the server just produced — the engine's challenge methods, or the
 * bridge's enrollment methods.
 */
export interface IAuthRouteState {
	from?: string;
	twoFactorMethods?: TwoFactorMethod[];
	methods?: TwoFactorMethod[];
}

/** The slice of the router history the auth flow uses — a push, to a path or to a path + state. */
export interface IAuthHistory {
	push: (target: string | { pathname: string; state?: IAuthRouteState }) => void;
}

/**
 * Dispatch as the thunks in this module need it.
 *
 * `store` is annotated `EnhancedStore`, and that annotation erases the thunk middleware from
 * `dispatch`'s type (and therefore from `AppDispatch`) — which is why the rest of the app casts
 * at every thunk dispatch. The auth surfaces ask `useDispatch` for this view instead.
 *
 * Self-referential on purpose: the thunks below annotate their own `dispatch` parameter with this
 * interface (mobile compiles with `noImplicitAny: false`, so leaving it off would silently give
 * every auth thunk an `any` dispatch and type-check nothing they dispatch), and a thunk typed that
 * way is only assignable back into this call signature if the signature promises the same thing.
 */
export interface IAuthThunkDispatch {
	<TResult>(thunk: (dispatch: IAuthThunkDispatch) => TResult): TResult;
	(action: { type: string }): void;
}

export interface IBetterAuthLoginArgs {
	identifier: string;
	password: string;
	from?: string;
	history: IAuthHistory;
}

/** What `completeBetterAuthLogin` did, so a caller mid-MFA-flow can render accordingly. */
export type BetterAuthCompletionOutcome = 'ok' | 'enrollmentRequired' | 'error';

/**
 * Login against the Better Auth engine — mobile's port of the desktop thunk, and the reason
 * mobile no longer needs a token handed to it in a URL.
 *
 * Identifier dispatch is the pinned contract, shared with desktop through `getIsEmailAddress`:
 * an input shaped like an email address goes to `signIn.email`, everything else to
 * `signIn.username`. Both are lowercased, matching what the engine's username plugin normalizes
 * to anyway.
 *
 * A `twoFactorRedirect` response means the password was accepted but no session exists yet —
 * the user is routed to the challenge view, carrying the post-login redirect target with them.
 * Otherwise the engine session cookie is live and the bridge takes over.
 */
export const betterAuthLogin =
	({ identifier, password, from, history }: IBetterAuthLoginArgs) =>
	async (dispatch: IAuthThunkDispatch): Promise<void> => {
		dispatch(
			setLoginStateData({
				loginRequestStatus: {
					state: LoginRequestStatuses.LOADING,
				},
			})
		);
		const normalizedIdentifier: string = identifier.trim().toLowerCase();
		try {
			// Imported on demand, not at module scope: this module is reachable from the eager
			// bootstrap path, so a static import would ship the Better Auth client on every page
			// load in every environment — including the ones where the cutover flag is off and it
			// can never run.
			const { authClient } = await import('../../services/betterAuthClient');
			const { data, error } = getIsEmailAddress(normalizedIdentifier)
				? await authClient.signIn.email({ email: normalizedIdentifier, password })
				: await authClient.signIn.username({ username: normalizedIdentifier, password });

			if (error) {
				dispatchNeutralLoginError(dispatch, error.status);
				return;
			}

			const twoFactorRedirect: boolean = !!(data as { twoFactorRedirect?: boolean } | null)?.twoFactorRedirect;
			if (twoFactorRedirect) {
				const twoFactorMethods: TwoFactorMethod[] =
					(data as { twoFactorMethods?: TwoFactorMethod[] } | null)?.twoFactorMethods || [];
				dispatch(
					setLoginStateData({
						loginRequestStatus: { state: LoginRequestStatuses.INITIAL },
					})
				);
				history.push({
					pathname: AppRoutes.twoFactor,
					state: { from, twoFactorMethods },
				});
				return;
			}

			await dispatch(completeBetterAuthLogin({ from, history }));
		} catch (e) {
			console.error('Better Auth login error:', e);
			dispatchNeutralLoginError(dispatch, (e as { status?: number } | undefined)?.status);
		}
	};

/**
 * Turns a live engine session into an application session.
 *
 * The single funnel every flag-ON entry point goes through — first-factor-only login, a passed
 * second-factor challenge, a completed forced enrollment, and the silent bootstrap attempt that
 * picks up a session created by a desktop login. On success it feeds the bridge's `ILoggedUser`
 * into `setLoggedUserThunk`, the *same* post-login path the legacy `?token=` bootstrap used
 * (tokens to storage, projects to redux, dependent services initialized).
 *
 * A 403 `enrollmentRequired` routes to enrollment instead; the caller learns which happened from
 * the returned outcome.
 */
export const completeBetterAuthLogin =
	({ from, history }: { from?: string; history: IAuthHistory }) =>
	async (dispatch: IAuthThunkDispatch): Promise<BetterAuthCompletionOutcome> => {
		try {
			const result: BridgeResult = await mintTokensFromEngineSession();

			if (result.outcome === 'enrollmentRequired') {
				dispatch(
					setLoginStateData({
						loginRequestStatus: { state: LoginRequestStatuses.INITIAL },
					})
				);
				history.push({
					pathname: AppRoutes.twoFactorEnrollment,
					state: { from, methods: result.methods },
				});
				return 'enrollmentRequired';
			}

			await dispatch(setLoggedUserThunk(result.loggedUser));
			dispatch(
				setLoginStateData({
					loginRequestStatus: {
						state: LoginRequestStatuses.SUCCESS,
					},
				})
			);
			// `/` is `InitialRedirect`, which picks the landing page from the user's access and
			// preferences. Mobile has no executive surface, so unlike desktop there is no
			// role-dependent branch here.
			history.push(from || '/');
			return 'ok';
		} catch (e) {
			// A 401 is an EXPECTED refusal, not a fault: the silent bootstrap probes the bridge on
			// every cookieless page load, and "no live engine session" is its normal answer. Logging
			// it as an error would put a red herring in the console of every fresh visit.
			if ((e as { status?: number } | undefined)?.status !== UNAUTHORIZED_STATUS_CODE) {
				console.error('Auth bridge error:', e);
			}
			dispatch(
				setLoginStateData({
					loginRequestStatus: {
						state: LoginRequestStatuses.ERROR,
						statusCode: UNAUTHORIZED_STATUS_CODE,
						responseMessage: translationService.get('login_error_sessionCouldNotBeCompleted'),
					},
				})
			);
			return 'error';
		}
	};

export const setLoggedUserThunk = (loggedUser: ILoggedUser) => async (dispatch) => {
	userService.setUserDetails(loggedUser.userDetails);
	userService.setLSUserData({
		accessToken: loggedUser.accessToken,
		refreshToken: loggedUser.refreshToken,
	});
	dispatch(setLoggedUser({ user: loggedUser }));
	dispatch(updateUserProjects({ userProjects: loggedUser.userProjects }));
	dispatch(
		fetchAndSetWorkingProject(projectService.getChosenProject()?.projectId || loggedUser.userProjects[0].projectId)
	);
	dispatch(getUserPreferences() as any);
	initServicesDependentByUserData(
		loggedUser.userDetails,
		projectService.getChosenProject()?.projectId || loggedUser.userProjects[0].projectId
	);
};
